import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	jsxJoin,
	makeClassName,
	Modal,
	ModalProps,
	RequiredKeys,
	Row,
	Rule,
	TruncatedText,
} from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import {
	EmptyView,
	Icon,
	OrderDirection,
	SlimEmptyView,
	Student,
	StudentComponents,
	StudentDataConsentComponents,
	StudentDataConsentDetailListQuery,
	StudentDataConsentOrderField,
	StudentDataConsentStudentForm,
	StudentFormat,
	StudentNameAndImageFragment,
	Tile,
	useParentPortalViewerInfoQuery,
	useStudentDataConsentDetailListQuery,
} from "@hex-insights/verita-romania.shared";
import { DataPrivacyStatement } from "../../../Components";
import { preferencesStudentDataConsentsPageInfo } from "./pageinfo";
import styles from "./styles.module.css";

export function PreferencesStudentDataConsentsPage() {
	useActivePageRegistration(preferencesStudentDataConsentsPageInfo);

	const { loading: loadingViewer, data: viewerData } = useParentPortalViewerInfoQuery();

	const { loading: loadingStudentDataConsents, data: studentDataConsentData } = useStudentDataConsentDetailListQuery({
		variables: {
			filters: { student: [{ relationships: [{ guardian: [{ person: [{ user: [{ isCurrentEQ: true }] }] }] }] }] },
			order: { field: StudentDataConsentOrderField.CreatedAt, direction: OrderDirection.Desc },
		},
	});

	const isLoading = loadingViewer || loadingStudentDataConsents;

	const students = viewerData?.viewer.person?.guardian?.relationships.map((e) => e.student);
	const studentDataConsents = React.useMemo(() => {
		const studentDataConsents: Record<
			Student["id"],
			StudentDataConsentDetailListQuery["studentDataConsentConnection"]["edges"][0]["node"]
		> = {};

		if (!studentDataConsentData) {
			return studentDataConsents;
		}

		for (let i = 0; i < studentDataConsentData.studentDataConsentConnection.edges.length; i++) {
			const studentDataConsent = studentDataConsentData.studentDataConsentConnection.edges[i].node;
			if (!studentDataConsents[studentDataConsent.student.id]) {
				studentDataConsents[studentDataConsent.student.id] = studentDataConsent;
			}
		}

		return studentDataConsents;
	}, [studentDataConsentData]);

	return (
		<Tile>
			<Tile.Header>
				<Heading.H2 noMargin>Data Consent</Heading.H2>
			</Tile.Header>
			<Tile.Body style={{ paddingTop: "1.5rem", paddingBottom: "2rem" }}>
				<Column justify="spaced-start" verticalSpacing="1.5rem">
					<DataPrivacyStatement />

					<Rule thickness="2px" />

					<Conditional>
						<If condition={isLoading}>Loading...</If>
						<If condition={!students || students.length === 0}>
							<EmptyView
								IconComponent={Icon.Student}
								iconSize={48}
								primaryText="Students"
								secondaryText="We don't have any students connected to your account."
								style={{ paddingTop: "1rem" }}
							/>
						</If>
						<Else>
							{!!students &&
								jsxJoin(
									students.map((student) => (
										<StudentDataConsentSection
											key={student.id}
											student={student}
											studentDataConsent={studentDataConsents[student.id]}
										/>
									)),
									<Rule thickness="2px" />,
								)}
						</Else>
					</Conditional>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

type StudentDataConsentSectionProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	studentDataConsent:
		| StudentDataConsentDetailListQuery["studentDataConsentConnection"]["edges"][0]["node"]
		| null
		| undefined;
};

function StudentDataConsentSection({ student, studentDataConsent }: StudentDataConsentSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const {
		isModalOpen: isCreateModalOpen,
		openModal: openCreateModal,
		closeModal: closeCreateModal,
	} = Modal.useToggle();

	return (
		<React.Fragment>
			<Column justify="spaced-start">
				<Row justify="space-between" align="center" overflow="wrap" withVerticalSpacing>
					<Row justify="spaced-start" align="center" style={{ minWidth: 0 }}>
						<StudentComponents.StudentIcon student={student} size={36} />
						<Heading.H3 noMargin style={{ minWidth: 0 }}>
							<TruncatedText>{StudentFormat.name(student)}</TruncatedText>
						</Heading.H3>
					</Row>

					<If condition={!!studentDataConsent}>
						<StudentDataConsentComponents.StudentDataConsentUpdatePermissionRequired>
							<Button variant="link" size="small" onClick={openModal} className="nowrap" style={{ marginLeft: "auto" }}>
								Show Details
							</Button>
						</StudentDataConsentComponents.StudentDataConsentUpdatePermissionRequired>
					</If>
				</Row>

				<Conditional>
					<If condition={!!studentDataConsent}>
						{!!studentDataConsent && (
							<StudentDataConsentComponents.StudentDataConsentSummary studentDataConsent={studentDataConsent} />
						)}
					</If>
					<Else>
						<SlimEmptyView style={{ paddingTop: "1rem", paddingBottom: "1rem" }}>
							<Column justify="spaced-start" align="center">
								<span style={{ fontSize: "0.875rem" }}>We haven't received data consent for this student.</span>
								<StudentDataConsentComponents.StudentDataConsentCreatePermissionRequired>
									<Button variant="link" size="small" onClick={openCreateModal}>
										Click Here to Add
									</Button>
								</StudentDataConsentComponents.StudentDataConsentCreatePermissionRequired>
							</Column>
						</SlimEmptyView>
					</Else>
				</Conditional>
			</Column>

			<Modal.If condition={isModalOpen}>
				<StudentDataConsentComponents.StudentDataConsentModal
					student={student}
					studentDataConsent={studentDataConsent}
					onClose={closeModal}
				/>
			</Modal.If>

			<Modal.If condition={isCreateModalOpen}>
				<StudentDataConsentCreateModal studentID={student.id} onClose={closeCreateModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type StudentDataConsentCreateModalProps = {
	studentID: Student["id"];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

function StudentDataConsentCreateModal({
	studentID,
	ifRef,
	onClose,
	className,
	style,
}: StudentDataConsentCreateModalProps) {
	const formStateArgs = React.useMemo(() => ({ initialFormValues: { studentID } }), [studentID]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Data Consent</Heading.H2>
			</Modal.Header>
			<Modal.Body className={styles["create-modal__body"]}>
				<Column justify="spaced-start" verticalSpacing="1.5rem" style={{ flexGrow: 1 }}>
					<DataPrivacyStatement />

					<Rule thickness="2px" />

					<StudentDataConsentStudentForm.ControlledCreateWithPreset
						formStateArgs={formStateArgs}
						formNameSuffix={studentID}
						onSuccess={onClose}
						className={styles["create-form"]}
						columnClassName={styles["create-form__column"]}
					/>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
